import React, { useEffect, useState } from 'react'
import { StyledGridColumns, StyledGridRow } from '~/pages/Dairy/MilkFixedPrice/ShowMilkFixedPrice/style'
import { Button, Form, Grid, Loader } from 'semantic-ui-react'
import { StyledGrid } from '../../style'
import { FormFields } from '~/components/FormFields'
import { useTranslation } from 'react-i18next'
import { FarmersQuery } from '~/QueryHooks/Farmers'
import { handleErrors, onError } from '~/utils/helpers'
import { useParams } from 'react-router'
import { useForm, useWatch } from 'react-hook-form'
import { FarmersService } from '~/api/services/farmers'
import notify from '~/components/Notify'
import { LIGHT_USER_ROLE_SLUG } from '~/Constants'
import { useAuth } from '~/routes/components/AuthProvider'
import { useDebounce } from 'use-lodash-debounce';

const BankData: React.FC = () => {
  const { t } = useTranslation()
  const { id } = useParams()
  const { systemData: { logged: { roleSlug } } } = useAuth()
  const lightUserLogged = roleSlug === LIGHT_USER_ROLE_SLUG

  const [isBusy, setIsBusy] = useState(false)
  const [isLoadingBankData, setIsLoadingBankData] = useState(false)

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { touchedFields, dirtyFields },
    clearErrors,
    setError
  } = useForm({
    mode: 'onSubmit'
  })

  const watchIban = useWatch({ control, name: 'settings[0].iban' })
  const watchBankName = useWatch({ control, name: 'settings[0].bankName' })
  const watchBic = useWatch({ control, name: 'settings[0].bic' })

  const debouncedIbanValue = useDebounce(watchIban, 300);

  const {
    data: fincancialdata,
    isLoading: isBusyData
  } = FarmersQuery.getFinancialData(onError, id);

  useEffect(() => {
    if (fincancialdata && !isBusyData) {
      reset(fincancialdata)
    }
  }, [fincancialdata, isBusyData])

  useEffect(() => {
    if(!isBusyData && debouncedIbanValue && dirtyFields?.settings && dirtyFields?.settings[0]?.iban) {
      fetchBankData()
    }
  }, [isBusyData, debouncedIbanValue, touchedFields])

  async function fetchBankData() {
    setIsLoadingBankData(true)
    try {
      const response = await FarmersService.checkIban(id, { iban: debouncedIbanValue })
      if(response.data.ibanValid) {
        clearErrors(['settings[0].iban'])
        setValue('settings[0].bankName', response.data.bankName)
        setValue('settings[0].bic', response.data.bic)
      } else {
        setValue('settings[0].bankName', '')
        setValue('settings[0].bic', '')
      }
    }catch(error) {
      handleErrors(error)
    }
    setIsLoadingBankData(false)
  }

  async function onSubmit(data) {
    setIsBusy(true)
    try {
      await FarmersService.updateFinancialFarmerData(id, data)
      notify(t('Financial information updated'), 'success')
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  return <Form onSubmit={handleSubmit(onSubmit)} autoComplete={'off'} loading={isBusy}>
    {!lightUserLogged && <Button
      style={{ float: 'right', margin: 10 }}
      primary
    >{t('Update')}</Button>}
    <StyledGrid columns={1} stackable style={{ maxWidth: 700 }}>
      <Grid.Row>
        <Grid.Column>
          <Grid columns={4} stackable>
            <StyledGridRow>
              <Grid.Column style={{ position: 'relative' }}>
                <div>{t('IBAN')} <span style={{ color: 'red' }}>*</span></div>
                <Loader active={isLoadingBankData} size='small'/>
              </Grid.Column>
              <StyledGridColumns>
                <FormFields.Input
                  control={control}
                  rules={{
                    required: {
                      value: true,
                      message: t('Please enter iban')
                    },
                    validate: () => {
                      if(watchBankName === '') {
                        return t('Please enter valid iban')
                      }
                      return true
                    }
                  }}
                  disabled={lightUserLogged}
                  style={{ width: 300 }}
                  name={'settings[0].iban'}
                  placeholder={t('Enter iban')}
                />
              </StyledGridColumns>
            </StyledGridRow>
            <StyledGridRow>
              <Grid.Column><div>{t('Name of the bank')}</div></Grid.Column>
              <StyledGridColumns>
                {watchBankName}
              </StyledGridColumns>
            </StyledGridRow>
            <StyledGridRow>
              <Grid.Column><div>{t('BIC')}</div></Grid.Column>
              <StyledGridColumns>
                {watchBic}
              </StyledGridColumns>
            </StyledGridRow>
            <StyledGridRow>
              <Grid.Column>{t('Status')}</Grid.Column>
              <StyledGridColumns>
                <FormFields.Checkbox
                  control={control}
                  defaultValue={0}
                  disabled={lightUserLogged}
                  name={'settings[0].status'}
                  label={t('Active')}
                  checkboxValue={1}
                />
              </StyledGridColumns>
            </StyledGridRow>
          </Grid>
        </Grid.Column>
      </Grid.Row>
    </StyledGrid>
  </Form>
}
export default BankData